import { memo } from 'react';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { HStack, Heading, Text } from '@library';
import { ProgressSpinner } from '../../../../core/components/ProgressIndicators';
import useCategory from '../../../../core/queries/useCategory';
import withErrorBoundary from '../../../../core/components/errors/withErrorBoundary';

const ProductListCategoryHeader = ({
    header,
    subHeader,
    totalLoading = false,
    total = 0,
    filteredTotal = 0,
    headerAction,
}: {
    header?: ReactNode;
    subHeader?: ReactNode;
    headerAction?: ReactNode;
    totalLoading?: boolean;
    total?: number;
    filteredTotal?: number;
}) => {
    const { categoryName } = useCategory();
    const intl = useIntl();
    const labelText = total === 1 ? 'product' : 'products';

    const countText =
        filteredTotal !== total
            ? intl.formatMessage(
                  { id: '{x} of {y} products' },
                  {
                      x: filteredTotal,
                      y: total,
                  }
              )
            : `${total} ${intl.formatMessage({ id: labelText })}`;

    const currentHeader = header || (
        <Heading h3 bold dataTestClass={'categoryHeader'}>
            {categoryName}
        </Heading>
    );

    if (!header && !categoryName) {
        return null;
    }

    return (
        <HStack justify="spaceBetween" align="center" wrap gap="mini">
            <HStack
                align={totalLoading ? 'center' : 'baseline'}
                gap="small"
                wrap
                expanded
            >
                <div>
                    {currentHeader}
                    {subHeader}
                </div>
                {totalLoading ? (
                    <ProgressSpinner />
                ) : (
                    <Text
                        data-test-class="productListHeaderCount"
                        data-test-state={filteredTotal}
                    >
                        {countText}
                    </Text>
                )}
            </HStack>
            {headerAction}
        </HStack>
    );
};

export default memo(withErrorBoundary(ProductListCategoryHeader));
